import { useEffect, useState } from 'react'
import { useParams, Link, useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useAuthStore } from '../../store/authStore'
import { useToastStore } from '../../store/toastStore'
import { translateApiError } from '../../lib/apiErrors'
import AuthBrand from '../../components/auth/AuthBrand'
import AuthShell from '../../components/auth/AuthShell'
import { authHeadingClass, authLinkClass, authSubmitClass } from '../../components/auth/authTheme'

// Survives React Strict Mode remounts (useRef resets on remount → double POST).
const consumedVerifyTokens = new Set<string>()

export default function VerifyEmail() {
  const { token } = useParams<{ token: string }>()
  const [status, setStatus] = useState<'loading' | 'error'>('loading')
  const { t } = useTranslation(['auth', 'common'])
  const verifyEmail = useAuthStore((s) => s.verifyEmail)
  const { addToast } = useToastStore()
  const navigate = useNavigate()

  useEffect(() => {
    const verify = async () => {
      if (!token) {
        setStatus('error')
        return
      }
      if (consumedVerifyTokens.has(token)) {
        return
      }
      consumedVerifyTokens.add(token)

      try {
        await verifyEmail(token)
        addToast(t('auth:verifyEmail.success.toast'), 'success')
        navigate('/dashboard', { replace: true })
      } catch (error) {
        // Allow a manual retry (e.g. resend → new link uses a different token).
        consumedVerifyTokens.delete(token)
        setStatus('error')
        addToast(translateApiError(error, t), 'error')
      }
    }

    verify()
    // Intentionally only re-run when the token in the URL changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token])

  return (
    <AuthShell>
      <div className="text-center">
        <AuthBrand />

        {status === 'loading' && (
          <>
            <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-[#1a1a1a] border border-gray-700">
              <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-[#FF5A00]" />
            </div>
            <h2 className={`mt-6 ${authHeadingClass}`}>{t('auth:verifyEmail.loading.heading')}</h2>
            <p className="mt-2 text-sm text-gray-400">{t('auth:verifyEmail.loading.body')}</p>
          </>
        )}

        {status === 'error' && (
          <>
            <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-red-900/40 border border-red-700">
              <svg className="h-6 w-6 text-red-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </div>
            <h2 className={`mt-6 ${authHeadingClass}`}>{t('auth:verifyEmail.error.heading')}</h2>
            <p className="mt-2 text-sm text-gray-400">{t('auth:verifyEmail.error.body')}</p>
            <div className="mt-6 space-y-3">
              <Link to="/auth/resend-verification" className={`${authSubmitClass} inline-flex w-auto px-6`}>
                {t('auth:verifyEmail.error.resendAction')}
              </Link>
              <div className="flex space-x-4 justify-center">
                <Link to="/auth/register" className={authLinkClass}>
                  {t('auth:verifyEmail.error.registerAgain')}
                </Link>
                <span className="text-sm text-gray-600">|</span>
                <Link to="/auth/login" className={authLinkClass}>
                  {t('common:backToLogin')}
                </Link>
              </div>
            </div>
          </>
        )}
      </div>
    </AuthShell>
  )
}
